/**
 * Resolve the transit backend (gtfs_axum) base URL from env, in this order:
 *
 *   1. `VITE_GTFS_API_BASE_URL` — full URL or path. Highest priority. Use this
 *      for same-origin deployment (`/api`), HTTPS, or a remote backend
 *      (`https://api.example.com`). When set, host/port vars are ignored.
 *   2. `VITE_GTFS_API_HOST` (default `localhost`) + `VITE_GTFS_API_PORT` (default `8000`)
 *      — composed as `http://${host}:${port}`. Use these for local dev
 *      when the backend's `PORT` env var is non-default.
 *
 * Any trailing slash is stripped so endpoint paths join cleanly.
 *
 * Reminder: the backend's CORS allow-list is hardcoded to
 * `http://localhost:5173` (the Vite dev server). Changing the *frontend*
 * origin requires editing `gtfs_axum/src/gtfs_lib/backend_server.rs`.
 */
const resolveApiBaseUrl = (): string => {
  const explicit = import.meta.env.VITE_GTFS_API_BASE_URL
  if (explicit) return explicit.replace(/\/+$/, '')

  const host = import.meta.env.VITE_GTFS_API_HOST ?? 'localhost'
  const port = import.meta.env.VITE_GTFS_API_PORT ?? '8010'
  return `http://${host}:${port}`
}

const API_BASE_URL = resolveApiBaseUrl()

export class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    }

    try {
      const response = await fetch(url, config)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return await response.json()
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error)
      throw error
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data !== undefined ? JSON.stringify(data) : undefined,
    })
  }
}

export const apiClient = new ApiClient()
